import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CalendarViewMode, CalendarEvent } from "../types";
import { useLanguage } from '@/contexts/LanguageContext';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth, format } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { Clipboard, Copy, ClipboardPaste, AlarmClock, ChevronDown, Eye, EyeOff, Calendar as CalendarIcon } from 'lucide-react';
import WeekVisibilityToggle from './WeekVisibilityToggle';
import { Skeleton } from "@/components/ui/skeleton";
import ClearConfirmationDialog from './ClearConfirmationDialog';

interface CalendarHeaderProps {
  currentDate: Date;
  onCopyWeek: (events: CalendarEvent[], withGuards?: boolean) => void;
  onCopyCustomRange?: (events: CalendarEvent[], withGuards?: boolean, mode?: 'week' | 'custom' | 'month') => void;
  onPasteWeek: (date: Date) => void;
  onClear: (type: 'week' | 'month' | 'custom' | 'missing') => void;
  events: CalendarEvent[];
  hasCopiedShifts: boolean;
  withGuards: boolean;
  onToggleCustomView?: () => void;
  isCustomView?: boolean;
  onDateRangeSelect?: (start: Date, end: Date) => void;
  viewMode: CalendarViewMode;
  teamId: string;
  isVisible?: boolean;
  onVisibilityChange?: (date: Date, isVisible: boolean) => Promise<boolean>;
  isClearing?: boolean;
  customDateRange?: { from: Date, to?: Date };
}

const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  currentDate,
  onCopyWeek,
  onCopyCustomRange,
  onPasteWeek,
  onClear,
  events,
  hasCopiedShifts,
  withGuards,
  onToggleCustomView,
  isCustomView,
  onDateRangeSelect,
  viewMode,
  teamId,
  isVisible = true,
  onVisibilityChange,
  isClearing = false,
  customDateRange
}) => {
  const { t } = useLanguage();
  const [clearConfirmation, setClearConfirmation] = useState<{
    isOpen: boolean;
    type: 'week' | 'month' | 'custom' | 'missing';
    startDate: Date;
    endDate: Date;
  }>({
    isOpen: false,
    type: 'week',
    startDate: new Date(),
    endDate: new Date()
  });

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  
  const filteredEvents = events.filter(event => {
    const eventDate = fromLocalISOString(event.start_date);
    return eventDate >= weekStart && eventDate <= weekEnd;
  });
  
  const weekLabel = `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d')}`;
  
  const handleCopyWeek = () => {
    onCopyWeek(filteredEvents, withGuards);
  };
  
  const handleCopyCustom = (mode: 'week' | 'custom' | 'month' = 'week') => {
    if (onCopyCustomRange) {
      onCopyCustomRange(events, withGuards, mode);
    }
  };
  
  const handlePasteWeek = () => {
    onPasteWeek(currentDate);
  };

  const handleClearClick = (type: 'week' | 'month' | 'custom' | 'missing') => {
    let startDate: Date;
    let endDate: Date;
    
    switch(type) {
      case 'week':
        startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
        endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
        break;
      
      case 'month':
        startDate = startOfMonth(currentDate);
        endDate = endOfMonth(currentDate);
        break;
        
      case 'custom':
        if (customDateRange?.from) {
          startDate = customDateRange.from;
          endDate = customDateRange.to || startDate;
        } else {
          startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
          endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
        }
        break;
        
      case 'missing':
        startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
        endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
        break;
        
      default:
        startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
        endDate = endOfWeek(currentDate, { weekStartsOn: 1 });
    }

    setClearConfirmation({
      isOpen: true,
      type,
      startDate,
      endDate
    });
  };

  const handleConfirmClear = () => {
    onClear(clearConfirmation.type);
    setClearConfirmation(prev => ({ ...prev, isOpen: false }));
  };

  const handleCloseClearConfirmation = () => {
    setClearConfirmation(prev => ({ ...prev, isOpen: false }));
  };
  
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2 py-2">
        <div className="flex items-center space-x-2">
          <h3 className="text-base font-medium">{weekLabel}</h3>
          
          {onVisibilityChange && (
            <WeekVisibilityToggle
              date={weekStart}
              teamId={teamId}
              isVisible={isVisible}
              onVisibilityChange={onVisibilityChange}
            />
          )}
        </div>
        
        <div className="flex flex-wrap gap-2">
          {viewMode === 'week' && onToggleCustomView && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={onToggleCustomView}
            >
              {isCustomView ? t("standardView") || "Standard View" : t("customView") || "Custom View"}
            </Button>
          )}
          
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1">
                <Copy className="h-4 w-4" />
                <span>{t("copy") || "Copy"}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleCopyWeek}>
                <Clipboard className="h-4 w-4 mr-2" />
                <span>{t("copyCurrentWeek") || "Copy Current Week"}</span>
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={() => handleCopyCustom('month')}>
                <Clipboard className="h-4 w-4 mr-2" />
                <span>{t("copyCurrentMonth") || "Copy Current Month"}</span>
              </DropdownMenuItem>
              
              {viewMode === 'custom' && (
                <DropdownMenuItem onClick={() => handleCopyCustom('custom')}>
                  <Clipboard className="h-4 w-4 mr-2" />
                  <span>{t("copyCustomRange") || "Copy Custom Range"}</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-1" 
            onClick={handlePasteWeek}
            disabled={!hasCopiedShifts}
          >
            <ClipboardPaste className="h-4 w-4" />
            <span>{t("paste") || "Paste"}</span>
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-1"
                disabled={isClearing}
              >
                {isClearing ? (
                  <>
                    <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-gray-300 border-t-primary"></div>
                    <span>{t("clearing") || "Clearing..."}</span>
                  </>
                ) : (
                  <>
                    <AlarmClock className="h-4 w-4" />
                    <span>{t("clear") || "Clear"}</span>
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleClearClick('week')} disabled={isClearing}>
                <span>{t("clearCurrentWeek") || "Clear Current Week"}</span>
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={() => handleClearClick('missing')} disabled={isClearing}>
                <span>{t("clearMissingShifts") || "Clear Missing Shifts"}</span>
              </DropdownMenuItem>
              
              <DropdownMenuItem onClick={() => handleClearClick('month')} disabled={isClearing}>
                <span>{t("clearCurrentMonth") || "Clear Current Month"}</span>
              </DropdownMenuItem>
              
              {viewMode === 'custom' && (
                <DropdownMenuItem onClick={() => handleClearClick('custom')} disabled={isClearing}>
                  <span>{t("clearCustomRange") || "Clear Custom Range"}</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <ClearConfirmationDialog
        isOpen={clearConfirmation.isOpen}
        onClose={handleCloseClearConfirmation}
        onConfirm={handleConfirmClear}
        clearType={clearConfirmation.type}
        startDate={clearConfirmation.startDate}
        endDate={clearConfirmation.endDate}
        isClearing={isClearing}
      />
    </>
  );
};

export default CalendarHeader;
